import { useEffect, useState } from 'react';
import { X, Plus, Bell, BellOff, Copy, PanelRightClose } from 'lucide-react';

/** How the top-level URL was obtained, so the badge never overstates it. */
const URL_SOURCE_LABEL = {
  provided: 'EXACT',
  direct: 'EXACT',
  'host-param': 'STORE ADMIN',
  referrer: 'FROM REFERRER',
  'referrer-parent': 'PARENT FRAME',
  unknown: 'UNKNOWN',
};

const URL_SOURCE_HELP = {
  provided: 'Supplied by the host app itself — the only source that knows the exact admin URL',
  direct: 'Read directly — the widget is top-level or same-origin with it',
  'host-param':
    "Reconstructed from Shopify's host parameter: the store's admin base, not the exact page",
  referrer: 'Inferred from the referrer; a referrer policy can truncate it to the origin',
  'referrer-parent': "The parent frame's URL, which in a nested embed is not the top page",
  unknown: 'The browser exposed no way to determine it',
};

const EXACT_SOURCES = new Set(['provided', 'direct']);
import { api } from '../lib/api.js';
import { useWorkspace } from '../lib/workspace.jsx';
import { usePanels } from '../lib/panels.jsx';
import EventTrail from './EventTrail.jsx';
import {
  clockTime,
  shortTime,
  duration,
  inZone,
  nowInZone,
  zoneOffsetLabel,
  describeUserAgent,
} from '../lib/format.js';

/** Keeps a long admin URL readable in a 316px rail without losing the tail. */
function shortenUrl(url) {
  try {
    const { pathname, hash } = new URL(url);
    const path = `${pathname}${hash}`;
    return path.length > 38 ? `…${path.slice(-37)}` : path || '/';
  } catch {
    return url.length > 38 ? `…${url.slice(-37)}` : url;
  }
}

/**
 * Everything known about the visitor on one rail, so an agent never leaves the
 * thread to answer "who is this and what have we said before".
 *
 * The metadata block is whatever the snippet passed at session time
 * (`window.SuperChat.visitor.metadata`) — shop domain, plan, app version. The
 * server stores it verbatim on the conversation.
 */
export function ContextRail({ conversation, onUpdate }) {
  const { isVisitorOnline, setConversationMuted } = useWorkspace();
  // The conversation's own tenant, not the one in focus — in the all-apps
  // scope there is no app in focus, and the rail is still about this thread.
  const appId = conversation?.appId ?? null;
  const { rail: railOpen, mobile, close } = usePanels();
  const [history, setHistory] = useState([]);
  const [tagDraft, setTagDraft] = useState('');
  const [subject, setSubject] = useState('');
  /**
   * IP and raw user agent, fetched separately because they are separately
   * authorised. `null` means "not loaded or not allowed" and the section is
   * hidden — a read-only member sees no empty row inviting them to wonder what
   * is missing.
   */
  const [identity, setIdentity] = useState(null);
  const [showRawAgent, setShowRawAgent] = useState(false);

  useEffect(() => {
    setSubject(conversation?.subject ?? '');
  }, [conversation?.id, conversation?.subject]);

  /* Past conversations for the same visitor, in this app only. */
  useEffect(() => {
    if (!conversation || !appId) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    api.conversations
      .list(appId, { visitorId: conversation.visitor.visitorId, limit: 10 })
      .then(({ data }) => {
        if (!cancelled) setHistory(data.filter((c) => c.id !== conversation.id));
      })
      .catch(() => setHistory([]));
    return () => {
      cancelled = true;
    };
  }, [conversation, appId]);

  /* Network details, for those allowed them. A 403 is an answer, not a fault. */
  useEffect(() => {
    setIdentity(null);
    setShowRawAgent(false);
    if (!conversation || !appId) return undefined;

    let cancelled = false;
    api.conversations
      .identity(appId, conversation.id)
      .then((data) => {
        if (!cancelled) setIdentity(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [conversation?.id, appId]);

  // Collapsed: give the thread the width back entirely rather than leaving a
  // stub. The toggle that brings it back lives in the thread header.
  if (!railOpen) return null;
  /* The empty rail holds its column open on a wide screen so the thread does
     not jump when a conversation is deselected. On a phone it holds nothing —
     it is a drawer over the inbox with no close button and no content, so
     there is simply nothing to render. */
  if (!conversation) return mobile ? null : <aside className="rail" />;

  const metadata = Object.entries(conversation.visitor.metadata ?? {});
  const online = isVisitorOnline(conversation.visitor.visitorId);
  const muted = Boolean(conversation.notifications?.agentMuted);

  const session = {
    origin: '',
    topOrigin: '',
    embedded: false,
    ancestorOrigins: [],
    pageViews: [],
    ...(conversation.session ?? {}),
  };

  // While they are still connected the session is ongoing, so measure to now.
  const sessionEnd = online ? Date.now() : new Date(session.lastSeenAt ?? Date.now());
  const sessionLength = session.startedAt
    ? duration((sessionEnd - new Date(session.startedAt)) / 1000)
    : '—';

  /**
   * Every clock on this rail is the agent's own — that is what
   * `toLocaleTimeString` does with no zone, and it is the right default. These
   * two let the rail also say what time it is for the visitor, which is the
   * thing an agent cannot work out from a row of their own timestamps.
   */
  const visitorNow = nowInZone(session.timezone);
  const visitorOffset = zoneOffsetLabel(session.timezone);
  const agentZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const browser = describeUserAgent(identity?.userAgent);

  const addTag = (event) => {
    event.preventDefault();
    const tag = tagDraft.trim();
    if (!tag || conversation.tags.includes(tag)) return;
    onUpdate({ tags: [...conversation.tags, tag] });
    setTagDraft('');
  };

  const removeTag = (tag) => onUpdate({ tags: conversation.tags.filter((t) => t !== tag) });

  return (
    <>
      {/* On a phone the rail floats over the thread, so it needs the same
          tap-away-to-dismiss the menu drawer has. */}
      {mobile && (
        <button
          type="button"
          className="scrim"
          aria-label="Close context panel"
          onClick={() => close('rail')}
        />
      )}
    <aside className="rail">
      <div className="rail__head">
        <div className="rail__identity">
          <span className="rail__avatar">
            {(conversation.visitor.name || 'V').charAt(0).toUpperCase()}
          </span>
          <div>
            <div className="rail__name">
              {conversation.visitor.name || conversation.visitor.visitorId}
            </div>
            <div className="rail__email">{conversation.visitor.email || 'No email captured'}</div>
          </div>
          <button
            type="button"
            className="icon-button rail__close"
            onClick={() => close('rail')}
            aria-label="Collapse context panel"
            title="Collapse context panel"
          >
            <PanelRightClose size={14} />
          </button>
        </div>

        <div className="rail__tags">
          {conversation.tags.map((tag) => (
            <span key={tag} className="chip">
              {tag}
              <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                <X size={10} />
              </button>
            </span>
          ))}
          <form className="chip chip--add" onSubmit={addTag}>
            <input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              placeholder="Add tag"
              aria-label="Add tag"
            />
            <button type="submit" aria-label="Add tag">
              <Plus size={10} />
            </button>
          </form>
        </div>
      </div>

      <div className="rail__scroll">
        <section className="rail__section">
          <h3 className="rail__label">NOTIFICATIONS</h3>
          <div className="rail__row">
            <span className="rail__key">
              {online ? 'Online now' : 'Offline'}
            </span>
            <span className="rail__value">
              <span
                className="presence__dot"
                style={{ background: online ? 'var(--ok)' : 'var(--line2)' }}
              />
            </span>
          </div>
          <button
            type="button"
            className={`rail__toggle${muted ? ' is-muted' : ''}`}
            onClick={() => setConversationMuted(conversation.id, !muted)}
          >
            {muted ? <BellOff size={13} /> : <Bell size={13} />}
            {muted ? 'Notifications muted' : 'Notifications on'}
          </button>
          <div className="rail__hint">
            {muted
              ? 'No alerts for this visitor — emergencies still come through.'
              : 'Alerts when this visitor arrives or messages you.'}
          </div>
        </section>

        <section className="rail__section">
          <h3 className="rail__label">CONVERSATION</h3>
          <label className="rail__subject">
            <span className="rail__key">Subject</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onBlur={() => subject !== conversation.subject && onUpdate({ subject })}
              placeholder="Untitled"
            />
          </label>
          <div className="rail__row">
            <span className="rail__key">Status</span>
            <span className="rail__value">{conversation.status}</span>
          </div>
          <div className="rail__row">
            <span className="rail__key">Started</span>
            <span className="rail__value mono">
              {new Date(conversation.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div className="rail__row">
            <span className="rail__key">Last message</span>
            <span className="rail__value mono">{clockTime(conversation.lastMessageAt)}</span>
          </div>
          <div className="rail__row">
            <span className="rail__key">Unread (merchant)</span>
            <span className="rail__value mono">{conversation.unread.visitor}</span>
          </div>
        </section>

        <section className="rail__section">
          <h3 className="rail__label">PAGE CONTEXT</h3>

          <div className="rail__row">
            <span className="rail__key">
              Main origin
              {session.embedded && !session.topOrigin && (
                <span className="rail__badge" title="This browser exposes neither ancestorOrigins nor a referrer">
                  UNKNOWN
                </span>
              )}
            </span>
            <span className="rail__value mono">{session.topOrigin || '—'}</span>
          </div>

          {session.topUrl && (
            <div className="rail__url">
              <div className="rail__url-head">
                <span className="rail__key">Main page URL</span>
                <span
                  className={`rail__badge${
                    EXACT_SOURCES.has(session.topUrlSource) ? ' rail__badge--ok' : ''
                  }`}
                  title={URL_SOURCE_HELP[session.topUrlSource] ?? ''}
                >
                  {URL_SOURCE_LABEL[session.topUrlSource] ?? 'INFERRED'}
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => navigator.clipboard.writeText(session.topUrl)}
                >
                  <Copy size={11} />
                </button>
              </div>
              <div className="rail__url-value mono" title={session.topUrl}>
                {session.topUrl}
              </div>
              {session.topUrlSource === 'referrer-parent' && (
                <div className="rail__hint">
                  This is the parent frame's URL. The embed is nested, so it may not be the
                  top-level page.
                </div>
              )}
            </div>
          )}

          <div className="rail__row">
            <span className="rail__key">
              Widget origin
              <span className="rail__badge rail__badge--ok" title="From the Origin header — set by the browser, cannot be forged">
                VERIFIED
              </span>
            </span>
            <span className="rail__value mono">{session.origin || '—'}</span>
          </div>

          <div className="rail__row">
            <span className="rail__key">Embedded</span>
            <span className="rail__value">
              {session.embedded ? `Yes · depth ${Math.max(1, session.ancestorOrigins.length)}` : 'No'}
            </span>
          </div>

          {session.ancestorOrigins.length > 1 && (
            <div className="rail__chain">
              {[...session.ancestorOrigins].reverse().map((origin, index) => (
                <div className="rail__chain-item mono" key={`${origin}-${index}`}>
                  <span className="rail__chain-rail" style={{ marginLeft: index * 8 }} />
                  {origin}
                </div>
              ))}
            </div>
          )}

          {session.embedded && !session.topOrigin && (
            <div className="rail__hint">
              The top-level origin is reported by the widget, and this browser exposes neither
              <code> ancestorOrigins</code> nor a referrer. Only the verified origin above is
              authoritative.
            </div>
          )}
        </section>

        <section className="rail__section">
          {/* Named, because the times below are the agent's own and the block
              underneath deliberately is not. */}
          <h3 className="rail__label">
            SESSION <span className="rail__label-note">· YOUR TIME</span>
          </h3>
          <div className="rail__row">
            <span className="rail__key">Started</span>
            <span className="rail__value mono">
              {session.startedAt ? clockTime(session.startedAt) : '—'}
              {session.startedAt && inZone(session.startedAt, session.timezone) && (
                <span className="rail__alt">
                  {inZone(session.startedAt, session.timezone)} there
                </span>
              )}
            </span>
          </div>
          <div className="rail__row">
            <span className="rail__key">Duration</span>
            <span className="rail__value mono">{sessionLength}</span>
          </div>
          <div className="rail__row">
            <span className="rail__key">Last seen</span>
            <span className="rail__value mono">
              {session.lastSeenAt ? shortTime(session.lastSeenAt) : '—'}
              {session.lastSeenAt && inZone(session.lastSeenAt, session.timezone) && (
                <span className="rail__alt">
                  {inZone(session.lastSeenAt, session.timezone)} there
                </span>
              )}
            </span>
          </div>
          {session.timezone && (
            <div className="rail__row">
              <span className="rail__key">Locale</span>
              <span className="rail__value mono">
                {session.language} · {session.timezone}
              </span>
            </div>
          )}
          {/* The one line that answers "is it a reasonable hour where they
              are". Suppressed when they share the agent's zone, where it would
              only repeat the clock above. */}
          {visitorNow && session.timezone !== agentZone && (
            <div className="rail__row">
              <span className="rail__key">Their time</span>
              <span className="rail__value mono">
                {visitorNow}
                {visitorOffset && <span className="rail__alt">{visitorOffset}</span>}
              </span>
            </div>
          )}
          {session.viewport && (
            <div className="rail__row">
              <span className="rail__key">Viewport</span>
              <span className="rail__value mono">{session.viewport}</span>
            </div>
          )}
        </section>

        {/**
          * Where they are and what they are using.
          *
          * Only rendered when the server actually answered — a member without
          * the grant gets a 403 above and this section never appears, rather
          * than showing empty rows that read like missing data.
          */}
        {identity && (identity.ip || identity.userAgent) && (
          <section className="rail__section">
            <h3 className="rail__label">NETWORK</h3>
            {identity.ip && (
              <div className="rail__row">
                <span className="rail__key">IP address</span>
                <span className="rail__value mono">{identity.ip}</span>
              </div>
            )}
            {identity.userAgent && (
              <>
                <div className="rail__row">
                  <span className="rail__key">Browser</span>
                  <span className="rail__value">{browser ?? 'Unrecognised'}</span>
                </div>
                <button
                  type="button"
                  className="rail__disclose"
                  onClick={() => setShowRawAgent((v) => !v)}
                  aria-expanded={showRawAgent}
                >
                  {showRawAgent ? 'Hide' : 'Show'} the raw user agent
                </button>
                {showRawAgent && (
                  <div className="rail__raw mono" title={identity.userAgent}>
                    {identity.userAgent}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {session.pageViews.length > 0 && (
          <section className="rail__section">
            <h3 className="rail__label">
              VISITED PAGES · {session.pageViews.length}
              <span className="rail__label-note">· YOUR TIME</span>
            </h3>
            <div className="rail__pages">
              {[...session.pageViews].reverse().map((page, index) => (
                <div
                  className="rail__page"
                  key={`${page.url}-${page.at}-${index}`}
                  title={
                    inZone(page.at, session.timezone)
                      ? `${inZone(page.at, session.timezone)} where the visitor is`
                      : undefined
                  }
                >
                  <span className="rail__page-time mono">{clockTime(page.at)}</span>
                  <span className="rail__page-url mono" title={page.url}>
                    {shortenUrl(page.url)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {metadata.length > 0 && (
          <section className="rail__section">
            <h3 className="rail__label">CUSTOM METADATA</h3>
            {metadata.map(([key, value]) => (
              <div className="rail__row" key={key}>
                <span className="rail__key">{key}</span>
                <span className="rail__value mono">{value}</span>
              </div>
            ))}
          </section>
        )}

        <section className="rail__section">
          <h3 className="rail__label">VISITOR</h3>
          <div className="rail__row">
            <span className="rail__key">Visitor ID</span>
            <span className="rail__value mono">{conversation.visitor.visitorId}</span>
          </div>
        </section>

        <section className="rail__section">
          <h3 className="rail__label">ACTIVITY</h3>
          <EventTrail appId={appId} conversationId={conversation.id} />
        </section>

        <section className="rail__section">
          <h3 className="rail__label">PAST CONVERSATIONS</h3>
          {history.length === 0 && <div className="rail__empty">First conversation.</div>}
          {history.map((item) => (
            <div className="rail__history" key={item.id}>
              <span
                className="rail__history-dot"
                style={{
                  background: ['resolved', 'closed'].includes(item.status)
                    ? 'var(--ok)'
                    : item.hasEmergency
                      ? 'var(--danger)'
                      : 'var(--warn)',
                }}
              />
              <span className="rail__history-title">
                {item.subject || item.lastMessagePreview || 'Conversation'}
              </span>
              <span className="rail__history-date mono">{shortTime(item.lastMessageAt)}</span>
            </div>
          ))}
        </section>
      </div>
    </aside>
    </>
  );
}

export default ContextRail;
